import { now, PurchaseItemStatus } from "common";
import dayjs, { Dayjs } from "dayjs";
import { filter, isNil, sumBy } from "lodash";
import {
    AllowedPrivacy,
    Experience,
    ExperiencePurchaseItem,
    GuestStatus,
    OPEN_UNLIMITED,
    PurchaseItemPrivacy,
    Seller,
    Timeslot,
    TimeslotStatusCount,
    TimeslotUnit,
    UnitType,
} from "types";

/**
 * Generate a fake, but unique ID for the timeslot.
 * Useful when we need to select a single timeslot in a state.
 */
export const getId = (timeslot: Pick<Timeslot, "date" | "time" | "product" | "event">): string => {
    if (timeslot) {
        const timeslotIdArray = [timeslot.product, timeslot.date];

        if (!isNil(timeslot.time)) {
            timeslotIdArray.push(timeslot.time.toString());
        }

        if (timeslot.event) {
            timeslotIdArray.push(timeslot.event.id);
        }

        return timeslotIdArray.join("_");
    }

    return "";
};

export const getReservedCapacity = (timeslot: Timeslot): number => {
    let capacity = timeslot.capacity.open;
    if (capacity < OPEN_UNLIMITED) {
        // The capacity of a limited event is the number of "open" seats + the number of "reserved" seats
        capacity += timeslot.capacity.reserved || 0;
    }

    return capacity;
};

export const getEquipmentUnits = (timeslot: Timeslot): TimeslotUnit[] => {
    return (timeslot.units ?? []).filter((unit) => unit.type === UnitType.EQUIPMENT);
};

export const isEquipmentTimeslot = (timeslot: Timeslot): boolean => {
    return getEquipmentUnits(timeslot).length > 0;
};

/** Available equipment units for the slot — unit counts, not guest seats (`count`, not `open`) */
export const getAvailableEquipmentUnits = (timeslot: Timeslot): number => {
    return sumBy(getEquipmentUnits(timeslot), (unit) => unit.count ?? 0);
};

/** Booked equipment units — one per reserved unit, same reserved statuses as guest capacity.reserved */
export const getBookedEquipmentUnits = (timeslot: Timeslot): number => {
    return timeslot.resourceUsageCount ?? 0;
};

/**
 * What "booked" means for a slot: equipment slots count booked units, everything else counts guests.
 */
export const getBookedCount = (timeslot: Timeslot): number => {
    return isEquipmentTimeslot(timeslot) ? getBookedEquipmentUnits(timeslot) : timeslot.capacity.reserved ?? 0;
};

/** Equipment slots report available units; other slots report open guest seats */
export const getOpenCapacity = (timeslot: Timeslot): number => {
    if (isEquipmentTimeslot(timeslot)) {
        if ((timeslot.capacity?.open ?? 0) <= 0) {
            return 0;
        }
        return getAvailableEquipmentUnits(timeslot);
    }
    return timeslot.capacity?.open ?? 0;
};

/** Equipment slots report total units (booked + available); other slots report max guest seats */
export const getMaxCapacity = (timeslot: Timeslot): number => {
    if (isEquipmentTimeslot(timeslot)) {
        return getBookedEquipmentUnits(timeslot) + getAvailableEquipmentUnits(timeslot);
    }
    return timeslot.capacity?.max ?? 0;
};

const reservedBookingStatuses = new Set<string>([
    PurchaseItemStatus.CONFIRMED,
    PurchaseItemStatus.PENDING_ACCEPT,
    PurchaseItemStatus.PENDING_MINIMUM,
]);

export const guestCountForReservedBookings = (timeslot: Timeslot) => {
    const statusCounts = (timeslot?.statusCounts ?? []).filter((statusCount) => {
        return reservedBookingStatuses.has(statusCount.status);
    });
    return sumBy(statusCounts, "count");
};

/**
 * @param itemPrivacy
 * @param allowedPrivacies
 * @returns
 * if passed item privacy is allowed return it, otherwise allowed one,
 * along with array of allowed privacies to handle edge cases for switch experience (X2-10026),
 * in some cases when we pass privacy=private to the API,
 * it can return a slot with `privacy: "public"` but with adjusted capacity for the private event,
 * and since this method is used to get privacy we pass to the ArrivalPicker,
 * we need to return allowed privacies so we can ignore privacy check when auto-selecting, if both allowed.
 */
export const getPurchaseItemPrivacyForTimeSlot = (
    itemPrivacy: AllowedPrivacy | PurchaseItemPrivacy,
    allowedPrivacies: Array<AllowedPrivacy | PurchaseItemPrivacy> = [],
) => {
    const pricing = allowedPrivacies.find((privacy) => privacy === itemPrivacy);

    return {
        privacy: pricing ?? allowedPrivacies[0],
        allowedPrivacies,
    };
};

export const getIsEventOverlap = (timeslot: Timeslot, arrivalDateTime: Dayjs) => {
    const eventDuration = timeslot.end.diff(timeslot.arrivalDatetime);
    const eventOverlapStart = eventDuration ? timeslot.arrivalDatetime.subtract(eventDuration) : null;
    return arrivalDateTime.isSameOrAfter(eventOverlapStart) && arrivalDateTime.isSameOrBefore(timeslot.end);
};

export const getConfirmedGuestCount = (timeslot: Timeslot) => {
    const filteredGuestStatusCounts = filter(timeslot.guestStatusCounts, (item) => {
        return item.guestStatus !== GuestStatus.NO_SHOW;
    });

    return sumBy(filteredGuestStatusCounts, "count") ?? 0;
};

export const getIsTimeslotBookable = (seller: Seller, timeslot: Timeslot, experience?: Experience) => {
    const { allowPastDates } = seller.preferences.booking;

    if (experience?.deletedAt) {
        return false;
    }

    if (timeslot.capacity.open <= 0 || !timeslot.canBook) {
        return false;
    }

    if (timeslot.arrivalDatetime) {
        return allowPastDates || now().isSameOrBefore(timeslot.arrivalDatetime, "day");
    }

    return true;
};

const blackoutReasons = new Set<string>(["seller_schedule_blackout", "experience_schedule_blackout"]);

export const isBlackedOut = (timeslot: Timeslot): boolean => {
    return blackoutReasons.has(timeslot.unavailability?.reason ?? "");
};

export const canOpenRoster = (timeslot: Timeslot): boolean => {
    return !(isBlackedOut(timeslot) && timeslot.capacity.reserved === 0);
};

export const hasWaitlists = (timeslot: Timeslot) => {
    return (timeslot.waitlistCount ?? 0) > 0;
};

export const getHoldStatusCount = (statusCounts: TimeslotStatusCount[]) => {
    return statusCounts?.find((statusCount) => statusCount.status === PurchaseItemStatus.HOLD)?.count ?? 0;
};

export const hasOverlappingSharedResources = (currentExperience: Experience, newExperience: Experience): boolean => {
    const currentExperienceResourceIds = currentExperience.resources?.map((resource) => resource.id) || [];
    if (currentExperienceResourceIds.length === 0) {
        return false;
    }

    const newExperienceResourceIds = newExperience.resources?.map((resource) => resource.id) || [];
    if (newExperienceResourceIds.length === 0) {
        return false;
    }

    return currentExperienceResourceIds.some((id) => newExperienceResourceIds.includes(id));
};

export const generateIdFromPurchaseItem = (purchaseItem: ExperiencePurchaseItem): string => {
    const timeslotIdArray = [purchaseItem.product.id, purchaseItem.arrivalDate];

    if (!isNil(purchaseItem.arrivalTime)) {
        timeslotIdArray.push(purchaseItem.arrivalTime.toString());
    }

    if (purchaseItem.event?.id) {
        timeslotIdArray.push(purchaseItem.event.id);
    }

    return timeslotIdArray.join("_");
};

export const isTimeslotInProgress = (timeslot: Timeslot): boolean => {
    return dayjs(timeslot.arrivalDatetime).isSameOrBefore(now()) && dayjs(timeslot.end).isSameOrAfter(now());
};

export const getEventIdFromTimeslotId = (id: string): string | undefined => {
    // Timeslot id will be in format productID_date_time_eventId
    const parts = id.split("_");
    if (parts.length > 0) {
        return parts[parts.length - 1];
    }

    return undefined;
};
